import AddCircleOutlineRoundedIcon from "@mui/icons-material/AddCircleOutlineRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import HealingRoundedIcon from "@mui/icons-material/HealingRounded";
import LinkOffRoundedIcon from "@mui/icons-material/LinkOffRounded";
import LinkRoundedIcon from "@mui/icons-material/LinkRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { Box, Button, Chip, Divider, MenuItem, Stack, TextField, Typography } from "@mui/material";
import { useState } from "react";
import { NODE_KIND_META } from "@/features/network-topology/constants";
import type { NodeKind, SimBinding } from "@/features/network-topology/types";
import { useNetworkActions, useNetworkTopology } from "@/features/network-topology/useNetworkTopology";

/**
 * 選択中ノードの詳細＋操作パネル。
 *
 * 操作: 切断 / 接続 / 子ノード追加 / 削除。
 * root（parentId=null）は削除不可。子追加は任意の kind を選択可能。
 */
export function NodeDetailPanel() {
  const { nodes, selectedNodeId } = useNetworkTopology();
  const { disconnectNode, startConnect, addNode, removeNode, setFaulted } = useNetworkActions();
  const [newKind, setNewKind] = useState<NodeKind>("sensor");
  const [newLabel, setNewLabel] = useState("");
  const [newBinding, setNewBinding] = useState<SimBinding["kind"] | "none">("none");

  const node = selectedNodeId ? nodes.find((n) => n.id === selectedNodeId) : null;
  if (!node) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          ノードを選択してください
        </Typography>
      </Box>
    );
  }

  const isRoot = node.parentId === null;
  const isOffline = node.manual === "offline";
  const isConnecting = node.manual === "connecting";

  const handleAdd = () => {
    const label = newLabel.trim() || `${NODE_KIND_META[newKind].displayName}-new`;
    addNode({
      kind: newKind,
      label,
      parentId: node.id,
      simBinding: newBinding === "none" ? null : { kind: newBinding },
    });
    setNewLabel("");
  };

  return (
    <Stack sx={{ p: 2, gap: 1.5, height: "100%", overflow: "auto" }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
        {node.label}
      </Typography>
      <Stack direction="row" spacing={1}>
        <Chip size="small" label={NODE_KIND_META[node.kind].displayName} />
        <Chip
          size="small"
          color={isOffline ? "error" : isConnecting ? "warning" : "success"}
          label={node.manual.toUpperCase()}
        />
      </Stack>
      <Stack spacing={0.25}>
        <Typography variant="caption" color="text.secondary">
          ID: {node.id}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          parent: {node.parentId ?? "—"}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          sim binding: {node.simBinding?.kind ?? "—"}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          sim active: {node.simActive ? "yes" : "no"}
        </Typography>
      </Stack>

      <Divider />

      <Typography variant="caption" sx={{ fontWeight: 700 }}>
        接続操作
      </Typography>
      <Stack direction="row" spacing={1}>
        <Button
          size="small"
          variant="outlined"
          color="error"
          startIcon={<LinkOffRoundedIcon />}
          onClick={() => disconnectNode(node.id)}
          disabled={isOffline}
        >
          切断
        </Button>
        <Button
          size="small"
          variant="contained"
          color="primary"
          startIcon={<LinkRoundedIcon />}
          onClick={() => startConnect(node.id)}
          disabled={!isOffline && !isConnecting}
        >
          接続
        </Button>
      </Stack>

      <Divider />

      <Typography variant="caption" sx={{ fontWeight: 700 }}>
        異常状態（デモ）
      </Typography>
      <Stack direction="row" spacing={1}>
        <Button
          size="small"
          variant="contained"
          color="warning"
          startIcon={<WarningAmberRoundedIcon />}
          onClick={() => setFaulted(node.id, true)}
          disabled={node.faulted || node.manual !== "online"}
        >
          障害注入
        </Button>
        <Button
          size="small"
          variant="outlined"
          color="success"
          startIcon={<HealingRoundedIcon />}
          onClick={() => setFaulted(node.id, false)}
          disabled={!node.faulted}
        >
          解除
        </Button>
      </Stack>

      <Divider />

      <Typography variant="caption" sx={{ fontWeight: 700 }}>
        子ノード追加
      </Typography>
      <TextField
        select
        size="small"
        label="kind"
        value={newKind}
        onChange={(e) => setNewKind(e.target.value as NodeKind)}
      >
        {(Object.keys(NODE_KIND_META) as NodeKind[]).map((k) => (
          <MenuItem key={k} value={k}>
            {NODE_KIND_META[k].displayName}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        size="small"
        label="label"
        value={newLabel}
        onChange={(e) => setNewLabel(e.target.value)}
        placeholder={`${NODE_KIND_META[newKind].displayName}-new`}
      />
      <TextField
        select
        size="small"
        label="sim binding (optional)"
        value={newBinding}
        onChange={(e) => setNewBinding(e.target.value as SimBinding["kind"] | "none")}
      >
        <MenuItem value="none">なし</MenuItem>
        <MenuItem value="press">press</MenuItem>
        <MenuItem value="arm">arm</MenuItem>
        <MenuItem value="transport">transport</MenuItem>
        <MenuItem value="spawn">spawn</MenuItem>
      </TextField>
      <Button
        size="small"
        variant="contained"
        color="secondary"
        startIcon={<AddCircleOutlineRoundedIcon />}
        onClick={handleAdd}
      >
        追加
      </Button>

      <Divider />

      <Button
        size="small"
        variant="outlined"
        color="error"
        startIcon={<DeleteOutlineRoundedIcon />}
        onClick={() => removeNode(node.id)}
        disabled={isRoot}
      >
        ノード削除（配下含む）
      </Button>
    </Stack>
  );
}
